import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useServices } from "@/hooks/useServices";
import type { Service } from "@/types/service";

interface ServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceId?: string | null;
}

export function ServiceDialog({ open, onOpenChange, serviceId }: ServiceDialogProps) {
  const { services, createService, updateService, isCreating, isUpdating } = useServices();
  const [formData, setFormData] = useState({
    service_code: "",
    service_name: "",
    service_type: "maintenance" as Service["service_type"],
    description: "",
    cost_price: "",
    sale_price: "",
    estimated_duration_hours: "",
  });

  useEffect(() => {
    if (serviceId) {
      const service = services.find(s => s.id === serviceId);
      if (service) {
        setFormData({
          service_code: service.service_code || "",
          service_name: service.service_name,
          service_type: service.service_type,
          description: service.description || "",
          cost_price: service.cost_price?.toString() || "",
          sale_price: service.sale_price?.toString() || "",
          estimated_duration_hours: service.estimated_duration_hours?.toString() || "",
        });
      }
    } else {
      setFormData({
        service_code: "",
        service_name: "",
        service_type: "maintenance",
        description: "",
        cost_price: "",
        sale_price: "",
        estimated_duration_hours: "",
      });
    }
  }, [serviceId, services, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const serviceData = {
      service_code: formData.service_code || undefined,
      service_name: formData.service_name,
      service_type: formData.service_type,
      description: formData.description || undefined,
      cost_price: formData.cost_price ? parseFloat(formData.cost_price) : undefined,
      sale_price: formData.sale_price ? parseFloat(formData.sale_price) : undefined,
      estimated_duration_hours: formData.estimated_duration_hours ? parseFloat(formData.estimated_duration_hours) : undefined,
      active: true,
    };

    if (serviceId) {
      updateService({ id: serviceId, ...serviceData });
    } else {
      createService(serviceData);
    }
    
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{serviceId ? "Editar Serviço" : "Novo Serviço"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="service_code">Código (Opcional)</Label>
              <Input
                id="service_code"
                value={formData.service_code}
                onChange={(e) => setFormData({ ...formData, service_code: e.target.value })}
                placeholder="Ex: SERV-001"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="service_name">Nome do Serviço *</Label>
              <Input
                id="service_name"
                value={formData.service_name}
                onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                placeholder="Ex: Manutenção Preventiva"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="service_type">Tipo de Serviço *</Label>
            <Select
              value={formData.service_type}
              onValueChange={(value: Service["service_type"]) =>
                setFormData({ ...formData, service_type: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="maintenance">Manutenção</SelectItem>
                <SelectItem value="repair">Reparo</SelectItem>
                <SelectItem value="installation">Instalação</SelectItem>
                <SelectItem value="calibration">Calibração</SelectItem>
                <SelectItem value="inspection">Inspeção</SelectItem>
                <SelectItem value="other">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva o serviço..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cost_price">Preço de Custo (R$)</Label>
              <CurrencyInput
                value={parseFloat(formData.cost_price) || 0}
                onChange={(value) => setFormData({ ...formData, cost_price: value.toString() })}
                placeholder="R$ 0,00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sale_price">Preço de Venda (R$)</Label>
              <CurrencyInput
                value={parseFloat(formData.sale_price) || 0}
                onChange={(value) => setFormData({ ...formData, sale_price: value.toString() })}
                placeholder="R$ 0,00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">Duração (horas)</Label>
              <Input
                id="duration"
                type="number"
                step="0.5"
                value={formData.estimated_duration_hours}
                onChange={(e) => setFormData({ ...formData, estimated_duration_hours: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isCreating || isUpdating}>
              {serviceId ? "Atualizar" : "Criar"} Serviço
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
